import type { ICurrentUser } from "models/ICurrentUser";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { UserNeedsToLogin } from "../../api";
import { setCurrentUser } from "../../redux/currentUser";
import { showToast } from "../../redux/toastActions";
import { RootState } from "../../store";
import { resolveCurrentUser } from "./currentUserCache";
import { LoadingSpinner } from "./LoadingSpinner";

interface IAuthGuardProps {
  component: React.ReactNode;
  anyOf: string[];
  redirectTo?: string;
}

const ACCESS_DENIED = "You don't have permission to access this page.";

export const UserAuthGuard = ({
  component,
  anyOf,
  redirectTo = "/error",
}: IAuthGuardProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  ) as ICurrentUser | null;

  // The parent rebuilds the anyOf array on every render, so the effect keys off
  // its contents rather than its identity.
  const requiredRolesKey = (anyOf ?? []).join(",");
  const requiredRoles = useMemo(
    () => (requiredRolesKey ? requiredRolesKey.split(",") : []),
    [requiredRolesKey],
  );

  // Read inside the effect without making these dependencies: a user arriving
  // in the store must not re-run the check that put it there, and navigate
  // changes identity on every location change.
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let isActive = true;

    const checkUserAccess = async () => {
      setIsLoading(true);

      try {
        const user = currentUserRef.current ?? (await resolveCurrentUser());

        if (!isActive) {
          return;
        }

        if (!user?.id) {
          navigateRef.current(redirectTo);
          return;
        }

        const userRoles = user.roles ?? [];

        if (!requiredRoles.some((role) => userRoles.includes(role))) {
          dispatch(showToast(ACCESS_DENIED, "error"));
          navigateRef.current(redirectTo);
          return;
        }

        if (!currentUserRef.current) {
          dispatch(setCurrentUser(user));
        }

        setHasAccess(true);
      } catch (error) {
        if (!isActive) {
          return;
        }
        if (!(error instanceof UserNeedsToLogin)) {
          navigateRef.current(redirectTo);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    checkUserAccess();

    return () => {
      isActive = false;
    };
  }, [requiredRoles, redirectTo, dispatch]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return hasAccess ? <>{component}</> : <></>;
};
